/**
 * The soundtrack.
 *
 * One track playing at a time, changed by asking for a different one. Callers
 * say *what should be playing* rather than "start this" and "stop that" — the
 * director works out whether that means a crossfade, an instant swap or
 * nothing at all, and asking for the track already playing is free.
 *
 * ── Why this is module scope and not a hook ─────────────────────────────────
 *
 * Because a jump tears the whole flight scene down and builds a new one, and
 * the music must not notice. The same reason `warpStreaks` hoisted its state:
 * anything owned by the view dies with the view, and the crossing is precisely
 * the moment that happens. A track that restarted at the far end of a jump
 * would restart at the one moment the player is listening to it.
 *
 * ── Audio elements, not Web Audio ───────────────────────────────────────────
 *
 * Web Audio gives sample-accurate gain ramps and would be the obvious choice
 * for crossfading. It also requires decoding the whole file into memory first,
 * and these are long: eight minutes of stereo at 48kHz is about 90MB decoded,
 * per track. An `<audio>` element streams instead, and its `volume` is a
 * perfectly good fade when what you are fading is background music.
 *
 * ── Nothing plays until the player has touched something ────────────────────
 *
 * Browsers refuse audio before a gesture, and refuse it by returning a rejected
 * promise rather than by throwing — so it fails silently and stays failed. The
 * first refusal arms a one-shot listener that retries on the next real input,
 * which in this game is pressing play.
 */

export type Track = 'space' | 'station' | 'combat' | 'jumpLocked' | 'jumpTunnel';

/**
 * Where each track lives, without its extension.
 *
 * Two encodings of each are shipped and the format is chosen once at startup —
 * see `EXTENSION`. Ogg Vorbis loops cleanly where MP3 cannot, because the
 * encoder's padding becomes a gap at the loop point; AAC is the fallback for
 * anything that will not take Vorbis.
 */
const FILES: Record<Track, string> = {
  space: 'ambient_space',
  station: 'ambient_station',
  // Nothing asks for this yet, on purpose: there is no combat to score. It is
  // declared because the track exists, and because the day something does start
  // shooting, the wiring is one `playTrack('combat')` beside whatever decides a
  // fight has begun — plus a rule for when one has ended, which is the harder
  // half and the reason this is not guessed at now.
  combat: 'ambient_combat',
  jumpLocked: 'jump_locked',
  jumpTunnel: 'jump_tunnel',
};

/**
 * How loud the music is at full volume.
 *
 * Music is the bed, not the event. Anything that happens — an impact, a switch,
 * a hail — has to be able to be heard over it without ducking, and the cheapest
 * way to guarantee that is to leave room in the first place. The player's own
 * setting scales this rather than replacing it, so "full" is still polite.
 */
const MUSIC_LEVEL = 0.45;

/**
 * The settings the control offers, quietest first.
 *
 * Steps rather than a continuous slider, for two reasons. A slider under pointer
 * lock is a drag gesture the cockpit cannot spare, and nobody has ever wanted
 * the difference between 38% and 41% of a background track — five choices and
 * off is the whole useful range.
 *
 * Not linear, because loudness is not: halfway up a linear scale sounds much
 * more than half as loud. These are roughly even *perceived* steps.
 */
export const MUSIC_STEPS: readonly number[] = [0, 0.15, 0.3, 0.5, 0.72, 1];

/** The ordinary crossfade. Long enough to be a change of mood, not a cut. */
export const FADE = 2.5;

/** Fading up from nothing, which wants longer — silence to music is abrupt. */
export const SLOW_FADE = 4;

const STORAGE_KEY = 'interspacial:music';

/** Which encoding this browser will take. Decided once; it cannot change. */
function pickExtension(): 'ogg' | 'm4a' {
  // `canPlayType` answers '', 'maybe' or 'probably'. Anything but empty is a
  // yes — a browser that says 'maybe' about Vorbis plays Vorbis.
  const probe = document.createElement('audio');
  return probe.canPlayType('audio/ogg; codecs="vorbis"') !== '' ? 'ogg' : 'm4a';
}

let extension: 'ogg' | 'm4a' | null = null;

interface Voice {
  element: HTMLAudioElement;
  /** Where the fade is going, 0..1 of `MUSIC_LEVEL`. */
  target: number;
  /** How much of the way it moves per second. */
  rate: number;
  /** Pause once it reaches zero, rather than streaming silence forever. */
  stopAtZero: boolean;
}

const voices = new Map<Track, Voice>();

/** What was last asked for, so asking again is free. */
let wanted: Track | null = null;

/** 0..1, scaling `MUSIC_LEVEL`. Zero is off. */
let level = read(STORAGE_KEY);
let ticking = false;
let waitingForGesture = false;

function read(key: string): number {
  try {
    const stored = window.localStorage.getItem(key);

    // 'off' is what the switch wrote before it became a dial. Read rather than
    // discarded, so somebody who turned the music off does not have it come
    // back on the next time they load the game.
    if (stored === 'off') return 0;
    if (stored === null) return 1;

    const asNumber = Number(stored);
    return Number.isFinite(asNumber) ? Math.min(1, Math.max(0, asNumber)) : 1;
  } catch {
    // Private browsing. Music on is the better default to fall back to.
    return 1;
  }
}

function voiceFor(track: Track): Voice {
  const existing = voices.get(track);
  if (existing) return existing;

  extension ??= pickExtension();

  /*
   * Vite substitutes `BASE_URL` at build time — the app is served under
   * /games/interspacial/, so a bare '/audio/...' would 404 in production.
   * Guarded because a plain node test has no `import.meta.env` at all, and a
   * module that cannot be imported outside a bundler cannot be tested.
   */
  const base = (import.meta.env?.BASE_URL as string | undefined) ?? '/';
  const element = new Audio(`${base}audio/${FILES[track]}.${extension}`);
  element.loop = true;
  element.preload = 'auto';
  element.volume = 0;

  const voice: Voice = { element, target: 0, rate: 1, stopAtZero: false };
  voices.set(track, voice);
  return voice;
}

/**
 * Try to play, and arrange to try again if the browser says not yet.
 *
 * The rejection is the *only* signal that autoplay was blocked, and it is not
 * an error worth showing anybody — it is the browser correctly refusing to make
 * noise at somebody who has not asked for any.
 */
function start(voice: Voice): void {
  const attempt = voice.element.play();
  if (!attempt) return;

  attempt.catch(() => {
    if (waitingForGesture) return;
    waitingForGesture = true;

    const retry = () => {
      waitingForGesture = false;
      window.removeEventListener('pointerdown', retry);
      window.removeEventListener('keydown', retry);

      // Only what is still wanted. A track that has been faded out and stopped
      // in the meantime should not come back because somebody clicked.
      for (const [track, one] of voices) {
        if (one.target > 0 || track === wanted) void one.element.play().catch(() => undefined);
      }
    };

    window.addEventListener('pointerdown', retry, { once: true });
    window.addEventListener('keydown', retry, { once: true });
  });
}

/** One rAF while anything is moving, and none while nothing is. */
function tick(): void {
  let last = performance.now();

  const step = (now: number) => {
    const seconds = Math.min(0.1, (now - last) / 1000);
    last = now;

    let moving = false;

    for (const [track, voice] of voices) {
      const at = voice.element.volume / MUSIC_LEVEL;
      const goal = voice.target * level;

      if (Math.abs(at - goal) < 0.001) {
        voice.element.volume = goal * MUSIC_LEVEL;

        /*
         * Faded out. Paused and rewound, and *kept*.
         *
         * Releasing the element would be tidier and is wrong: a crossing goes
         * space, locked, tunnel, space in under a minute, so discarding each
         * one means fetching and buffering it again on the way back. A fade-in
         * that has to buffer first is a fade-in you cannot hear the start of.
         * Five paused elements cost nothing.
         */
        if (goal === 0 && voice.stopAtZero && !voice.element.paused) {
          voice.element.pause();
          voice.element.currentTime = 0;
        }
        continue;
      }

      moving = true;
      const move = voice.rate * seconds;
      const next = at < goal ? Math.min(goal, at + move) : Math.max(goal, at - move);
      voice.element.volume = Math.max(0, Math.min(1, next)) * MUSIC_LEVEL;
    }

    if (moving) requestAnimationFrame(step);
    else ticking = false;
  };

  if (!ticking) {
    ticking = true;
    requestAnimationFrame(step);
  }
}

/**
 * Play this and nothing else.
 *
 * `fade` is how long the change takes, in seconds, and applies to both halves —
 * the outgoing track fades down over it while the incoming one fades up. **Zero
 * is an instant swap**, which is what entering the tunnel wants: the crossing
 * is a hard cut in the fiction and a crossfade there would soften the one moment
 * that should not be soft.
 *
 * Null means silence, faded out over the same interval.
 *
 * Asking for what is already playing does nothing at all, which is what makes
 * this safe to call from a render or a per-frame check rather than only from an
 * event.
 */
export function playTrack(track: Track | null, fade = FADE): void {
  if (track === wanted) return;
  wanted = track;

  // Rate rather than a duration, because a fade that starts from a track
  // already half-way down should take half as long rather than starting over.
  const rate = fade <= 0 ? Number.POSITIVE_INFINITY : 1 / fade;

  for (const [id, voice] of voices) {
    if (id === track) continue;
    voice.target = 0;
    voice.rate = rate;
    voice.stopAtZero = true;
  }

  if (track) {
    const voice = voiceFor(track);
    voice.target = 1;
    voice.rate = rate;
    voice.stopAtZero = false;

    // From the top whenever it had actually stopped. A jump track that resumed
    // from wherever it was left would open mid-phrase on the next crossing.
    if (voice.element.paused) {
      voice.element.currentTime = 0;
      if (fade <= 0) voice.element.volume = MUSIC_LEVEL * level;
      start(voice);
    }
  }

  tick();
}

/** What the director believes is playing. For tests and for the toggle. */
export function currentTrack(): Track | null {
  return wanted;
}

/** How loud the player has asked for it, 0..1. */
export function musicVolume(): number {
  return level;
}

/**
 * Set the level, without forgetting what was playing.
 *
 * Turning it down to nothing holds the track where it is rather than stopping
 * it, so turning it back up drops into the music the game would have been
 * playing anyway — the dial is a volume control, not a second piece of state
 * that can disagree with the first about what is on.
 *
 * The change is faded rather than jumped, on the same tick everything else
 * uses. A level that snapped would click, and clicking is the one thing a
 * volume control must not do.
 */
export function setMusicVolume(next: number): void {
  level = Math.min(1, Math.max(0, next));

  try {
    window.localStorage.setItem(STORAGE_KEY, String(level));
  } catch {
    // Nothing to do about it, and nothing worth telling anybody.
  }

  if (level > 0 && wanted) {
    const voice = voiceFor(wanted);
    if (voice.element.paused) start(voice);
  }

  tick();
}
